"use client"

import { useState, useEffect } from "react"
import { Button } from "../../../components/ui/button"
import { Checkbox } from "../../../components/ui/checkbox"
import { Project } from "../../../lib/types"
import { FileText, X, Check } from "lucide-react"

interface SelectProjectsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  allProjects: Project[]
  selectedProjectIds: string[]
  onConfirm: (projectIds: string[]) => void
}

export function SelectProjectsModal({
  open,
  onOpenChange,
  allProjects,
  selectedProjectIds,
  onConfirm,
}: SelectProjectsModalProps) {
  const [tempSelectedIds, setTempSelectedIds] = useState<string[]>([])

  useEffect(() => {
    if (open) {
      setTempSelectedIds(selectedProjectIds)
    }
  }, [open, selectedProjectIds])

  const handleToggle = (projectId: string) => {
    setTempSelectedIds((prev) =>
      prev.includes(projectId)
        ? prev.filter((id) => id !== projectId)
        : [...prev, projectId]
    )
  }

  const handleConfirm = () => {
    onConfirm(tempSelectedIds)
    onOpenChange(false)
  }

  const availableProjects = allProjects.filter(
    (project) => !selectedProjectIds.includes(project.id)
  )

  const newSelectionsCount = tempSelectedIds.length - selectedProjectIds.length

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col">
      <header className="flex items-center justify-between border-b px-6 py-4 w-full">
        <div>
          <h1 className="text-xl font-semibold">Selecionar Projetos</h1>
          <p className="text-sm text-muted-foreground">
            Marque os projetos que deseja adicionar a esta categoria.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4 mr-2" />
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={newSelectionsCount === 0}
          >
            <Check className="h-4 w-4 mr-2" />
            Adicionar {newSelectionsCount > 0 && `(${newSelectionsCount})`}
          </Button>
        </div>
      </header>

      <main className="flex-1 overflow-auto p-6">
        {availableProjects.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <FileText className="h-16 w-16 text-muted-foreground mb-4" />
            <p className="text-lg text-muted-foreground">
              Todos os projetos já estão nesta categoria.
            </p>
          </div>
        ) : (
          <div className="oveflow-y-auto grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {availableProjects.map((project) => {
              const isSelected = tempSelectedIds.includes(project.id)
              return (
                <button
                  type="button"
                  key={project.id}
                  onClick={() => handleToggle(project.id)}
                  className={`group relative rounded-lg border-2 overflow-hidden text-left transition-all ${
                    isSelected
                      ? "border-primary ring-2 ring-primary/20"
                      : "border-transparent hover:border-muted-foreground/30"
                  }`}
                >
                  <div className="relative aspect-video">
                    <img
                      src={project.thumbnail || "/placeholder.svg"}
                      alt={project.name}
                      className="object-cover"
                    />
                    <div
                      className={`absolute inset-0 bg-primary/10 transition-opacity ${
                        isSelected ? "opacity-100" : "opacity-0"
                      }`}
                    />
                    <div className="absolute top-2 left-2">
                      <Checkbox
                        checked={isSelected}
                        className="h-5 w-5 border-2 bg-background/80 backdrop-blur-sm"
                      />
                    </div>
                  </div>
                  <div className="p-3 bg-card">
                    <p className="font-medium text-sm line-clamp-1">
                      {project.name}
                    </p>
                    <p className="text-xs text-muted-foreground line-clamp-1 mt-1">
                      {project.description}
                    </p>
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </main>
    </div>
  )
}